import { Component, ContentChild, Input, OnDestroy, TemplateRef, ViewChild, inject } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ModalService } from 'src/app/services/modal.service';
import { Image } from 'shared/types';
import { randomId } from 'shared/utils/random-id';

export interface MobilePanelListItem {
    title: string;
    bodyText?: string;
    images?: Image[];
    items?: MobilePanelListItem[];
}

export interface MobilePanelTemplateContext {
    $implicit: MobilePanelListItem;
    item: MobilePanelListItem;
    isMobile: true;
}

@Component({
    selector: 'mobile-panel-list',
    templateUrl: './mobile-panel-list.component.html',
    styleUrls: ['./mobile-panel-list.component.scss'],
})
export class MobilePanelListComponent implements OnDestroy {
    private readonly modalService = inject(ModalService);

    @ContentChild('mobilePanelContent') panelTemplate?: TemplateRef<MobilePanelTemplateContext>;
    @ContentChild('mobilePanelChildContent') childPanelTemplate?: TemplateRef<MobilePanelTemplateContext>;
    @ViewChild('mobilePanelModal') mobilePanelModal: TemplateRef<unknown>;

    @Input() items: MobilePanelListItem[] = [];
    @Input() sectionHeader = '';
    @Input() rootClass = '';
    @Input() listClass = '';
    @Input() buttonClass = '';
    @Input() modalClass = '';
    @Input() contentClass = 'px-4 pb-4 pt-0';
    @Input() sectionHeaderClass = 'text-blue panel-title fw-bold fs-5 ps-3 mb-3';

    public readonly prefix = randomId();
    public activeItem?: MobilePanelListItem;
    public activeChild?: MobilePanelListItem;

    private mobileModal?: NgbModalRef;

    ngOnDestroy(): void {
        this.mobileModal?.close();
    }

    openItem(item: MobilePanelListItem): void {
        this.mobileModal?.close();
        this.activeItem = item;
        this.activeChild = undefined;

        const modal = this.modalService.open(this.mobilePanelModal, {
            ariaLabelledBy: this.titleId,
            backHandler: () => this.handleBack(),
            customRightModal: true,
            footerShow: false,
        });

        this.mobileModal = modal;

        modal.hidden.subscribe(() => {
            if (this.mobileModal !== modal) {
                return;
            }

            this.activeItem = undefined;
            this.activeChild = undefined;
            this.mobileModal = undefined;
        });
    }

    openChild(item: MobilePanelListItem): void {
        this.activeChild = item;
    }

    closePanel(): void {
        this.mobileModal?.close();
    }

    get titleId(): string {
        return `${this.prefix}-mobile-panel-title`;
    }

    get activeTitle(): string {
        return this.activeTemplateItem?.title || '';
    }

    get activeTemplateItem(): MobilePanelListItem | undefined {
        return this.activeChild || this.activeItem;
    }

    get activeTemplate(): TemplateRef<MobilePanelTemplateContext> | undefined {
        return this.activeChild ? this.childPanelTemplate || this.panelTemplate : this.panelTemplate;
    }

    get activeChildren(): MobilePanelListItem[] {
        return this.activeChild ? [] : this.activeItem?.items || [];
    }

    private handleBack(): void {
        if (this.activeChild) {
            this.activeChild = undefined;
            return;
        }

        this.closePanel();
    }
}
